// Display helpers for money and dates. Both use Intl to respect the user's
// locale for separators while keeping a stable currency layout.

const moneyFormatters = new Map<string, Intl.NumberFormat>();

function getMoneyFormatter(currency: string): Intl.NumberFormat {
  const key = currency.toUpperCase();
  let formatter = moneyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: key,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    moneyFormatters.set(key, formatter);
  }
  return formatter;
}

// Format a signed integer cent amount as a localized currency string.
// Example: formatMoney(1234)   -> "€12.34"
//          formatMoney(-500)   -> "-€5.00"
export function formatMoney(cents: number, currency: string = "EUR"): string {
  const value = cents / 100;
  return getMoneyFormatter(currency).format(value);
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const dateOnlyFormatter = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "long",
  day: "numeric",
});

// Format ISO 8601 timestamp as "May 20, 2026 14:32" (locale-dependent).
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dateFormatter.format(d);
}

// Format ISO 8601 timestamp as a date-only string, used for "account created on".
export function formatDateOnly(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return dateOnlyFormatter.format(d);
}

// Format a byte count as a human-readable size. Uses powers of 1024 with the
// usual KB/MB/GB labels (matches what most file-upload UIs show). Negative
// values are clamped to 0; non-finite inputs return "-".
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return "-";
  const n = Math.max(0, bytes);
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx += 1;
  }
  // 1 decimal under 10 (so 1.2 MB looks right), no decimals above (123 MB).
  const display = value >= 10 ? Math.round(value).toString() : value.toFixed(1);
  return `${display} ${units[unitIdx]}`;
}

// Human-friendly label for time saved by automated extraction.
// Each invoice ~5 min of manual work; we round to natural thresholds.
export function formatTimeSaved(totalFiles: number): string {
  const totalMinutes = totalFiles * 5;
  if (totalMinutes < 30) return "minutes";
  if (totalMinutes < 90) return "1 hour";
  const hours = Math.round(totalMinutes / 60);
  if (hours < 8) return `${hours} hours`;
  const days = Math.round(hours / 8);
  if (days === 1) return "a full work day";
  return `${days} work days`;
}

// Render a backend "page_range" hint (e.g. "1", "1-3", "2-2") more naturally
// for human consumption. Single-page ranges collapse to "p. 2"; multi-page
// ranges become "pp. 1-3". Falls back to the raw value on parse failure.
export function formatPageRange(range: string): string {
  if (!range) return "";
  const trimmed = range.trim();
  const match = trimmed.match(/^(\d+)(?:\s*-\s*(\d+))?$/);
  if (!match) return trimmed;
  const start = Number(match[1]);
  const end = match[2] !== undefined ? Number(match[2]) : start;
  if (!Number.isFinite(start) || !Number.isFinite(end)) return trimmed;
  if (start === end) return `p. ${start}`;
  return `pp. ${start}-${end}`;
}
